import { useEffect } from 'react';
import { cn } from '@/lib/utils';
import type { RateMultiplier } from '@/types/RateMultiplier';
import { ThresholdBadge } from '../../multipliers/MultiplierListItem';

interface MultiplierRadioListProps {
  multipliers: RateMultiplier[];
  selectedCode: string | null;
  onSelectionChange: (code: string | null) => void;
  showThresholds?: boolean;
  disabledCodes?: string[];
  disabledReason?: (multiplier: RateMultiplier) => string;
  suppressAutoSelect?: boolean;
}

export const MultiplierRadioList: React.FC<MultiplierRadioListProps> = ({
  multipliers,
  selectedCode,
  onSelectionChange,
  showThresholds = true,
  disabledCodes = [],
  disabledReason,
  suppressAutoSelect = false,
}) => {
  const filteredMultipliers = multipliers.filter(m => m.code !== 'STRAIGHT_TIME');
  const eligibleMultipliers = filteredMultipliers.filter(m => !disabledCodes.includes(m.code));
  const firstEligibleCode = eligibleMultipliers[0]?.code;

  // Auto-select the first eligible option when nothing is selected
  // (suppressed when an external custom option is active)
  useEffect(() => {
    if (!selectedCode && firstEligibleCode && !suppressAutoSelect) {
      onSelectionChange(firstEligibleCode);
    }
  }, [selectedCode, firstEligibleCode, onSelectionChange, suppressAutoSelect]);

  const handleSelect = (code: string) => {
    if (disabledCodes.includes(code)) return;
    if (selectedCode === code) return; // Selection is required — no deselection
    onSelectionChange(code);
  };

  return (
    <div role="radiogroup" className="flex flex-col gap-2">
      {filteredMultipliers.map((m) => {
        const isSelected = selectedCode === m.code;
        const isDisabled = disabledCodes.includes(m.code);
        const tooltip = isDisabled && disabledReason ? disabledReason(m) : undefined;

        return (
          <div
            key={m.code}
            role="radio"
            aria-checked={isSelected}
            aria-disabled={isDisabled}
            tabIndex={isDisabled ? -1 : 0}
            title={tooltip}
            className={cn(
              'group grid grid-cols-[44px_1fr_auto_auto] items-center gap-2 px-3 py-2 rounded',
              isDisabled
                ? 'border border-border/40 bg-surface-1 dark:bg-transparent opacity-50 cursor-not-allowed'
                : isSelected
                  ? 'border border-primary/50 bg-primary/5 cursor-pointer hover:bg-primary/8 active:bg-primary/10'
                  : 'border border-border/60 bg-surface-1 dark:bg-transparent equipment-card cursor-pointer hover:-translate-y-[1px] hover:shadow-md hover:border-primary/30 active:translate-y-[1px] active:shadow-none',
              'transition-[background-color,transform,box-shadow,border-color] duration-150'
            )}
            onClick={() => handleSelect(m.code)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSelect(m.code);
              }
            }}
          >
            {/* Rate */}
            <div className="font-mono text-sm font-bold tabular-nums text-primary-text">
              {m.defaultMultiplier != null ? `${m.defaultMultiplier}x` : '—'}
            </div>

            {/* Name */}
            <div className="font-heading text-[13px] uppercase tracking-wide truncate text-foreground">
              {m.name ?? m.code}
            </div>

            {/* Threshold badges */}
            {showThresholds && (
              <div className="flex flex-wrap items-center justify-end gap-2">
                {m.defaultPerShiftThresholdHours != null && (
                  <ThresholdBadge hours={m.defaultPerShiftThresholdHours} type="shift" />
                )}
                {m.defaultPerWeekThresholdHours != null && (
                  <ThresholdBadge hours={m.defaultPerWeekThresholdHours} type="week" />
                )}
                {m.defaultPerShiftThresholdHours == null && m.defaultPerWeekThresholdHours == null && (
                  <span className="text-[11px] sm:text-[12px] text-muted-foreground italic">No thresholds</span>
                )}
              </div>
            )}

            {/* Radio dot */}
            <div className="w-4 flex justify-center">
              <div
                className={cn(
                  'w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-150',
                  isSelected ? 'border-primary' : 'border-border'
                )}
              >
                <div
                  className={cn(
                    'w-2 h-2 rounded-full bg-primary transition-transform duration-150',
                    isSelected ? 'scale-100' : 'scale-0'
                  )}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
